import React from "react";
import { Mail, Download } from "lucide-react";
import { Bullet } from "./Bullet";
import { downloadFormationBrochure } from "../../utils/formationBrochure";

export default function FormationPresentiel({ t, lang }) {
  const forYou = [
    "formation.for_you_1",
    "formation.for_you_2",
    "formation.for_you_3",
    "formation.for_you_4",
    "formation.for_you_5",
    "formation.for_you_6",
  ];
  const immersionItems = [
    "formation.immersion_1",
    "formation.immersion_2",
    "formation.immersion_3",
    "formation.immersion_4",
    "formation.immersion_5",
  ];
  const ateliers = [
    "formation.ateliers_1",
    "formation.ateliers_2",
    "formation.ateliers_3",
    "formation.ateliers_4",
    "formation.ateliers_5",
    "formation.ateliers_6",
    "formation.ateliers_7",
    "formation.ateliers_8",
    "formation.ateliers_9",
  ];
  const formateurs = [
    "formation.formateur_claire",
    "formation.formateur_louise",
    "formation.formateur_emilie",
    "formation.formateur_magali",
  ];

  return (
    <div data-testid="formation-presentiel-panel">
      <section className="container-page pb-6">
        <h1 className="font-heading text-3xl sm:text-4xl font-medium text-ink max-w-3xl leading-tight">
          {t("formation.title")}
        </h1>
        <p className="mt-3 font-heading text-lg sm:text-xl text-ink-muted max-w-3xl">
          {t("formation.subtitle")}
        </p>
        <button
          type="button"
          onClick={() => downloadFormationBrochure(t, lang)}
          className="mt-5 inline-flex items-center gap-2 rounded-full bg-forest px-6 py-3 text-sm font-medium text-cream-50 shadow-soft transition hover:bg-forest-dark"
          data-testid="formation-brochure-download"
        >
          <Download size={16} aria-hidden />
          {t("formation.brochure_button")}
        </button>
      </section>

      {/* MAIN HEADING + INTRO */}
      <section className="container-page pb-6" data-testid="formation-overview">
        <h2 className="font-heading text-2xl sm:text-3xl text-ink max-w-3xl leading-snug">
          {t("formation.heading")}
        </h2>
        <p className="mt-5 text-ink-muted leading-relaxed max-w-3xl">
          {t("formation.intro")}
        </p>
      </section>

      {/* FOR YOU */}
      <section className="container-page pb-10">
        <aside
          className="rounded-[1.25rem] border border-forest/15 bg-forest/5 p-6 sm:p-8"
          data-testid="formation-for-you"
        >
          <h3 className="font-heading text-xl sm:text-2xl text-ink mb-5">
            {t("formation.for_you_title")}
          </h3>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3 text-ink-muted leading-relaxed">
            {forYou.map((k) => (
              <Bullet key={k}>{t(k)}</Bullet>
            ))}
          </ul>
        </aside>
      </section>

      {/* PROGRAMME */}
      <section className="container-page pb-10" data-testid="formation-programme">
        <h2 className="font-heading text-2xl sm:text-3xl text-ink mb-8">
          {t("formation.programme_title")}
        </h2>

        <div className="card-soft p-6 sm:p-7">
          <p className="text-xs uppercase tracking-[0.14em] text-forest-dark font-medium mb-2">
            {t("formation.welcome_title")}
          </p>
          <p className="text-ink-muted leading-relaxed">{t("formation.welcome_text")}</p>
        </div>

        <div className="mt-8 card-soft p-6 sm:p-8">
          <p className="text-xs uppercase tracking-[0.14em] text-forest-dark font-medium mb-2">
            {t("formation.immersion_title")}
          </p>
          <p className="text-ink-muted leading-relaxed">{t("formation.immersion_text")}</p>
          <ul className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3 text-ink-muted leading-relaxed">
            {immersionItems.map((k) => (
              <Bullet key={k}>{t(k)}</Bullet>
            ))}
          </ul>
        </div>

        <div className="mt-8 card-soft p-6 sm:p-8">
          <p className="text-xs uppercase tracking-[0.14em] text-forest-dark font-medium mb-2">
            {t("formation.ateliers_title")}
          </p>
          <p className="text-ink-muted leading-relaxed">{t("formation.ateliers_text")}</p>
          <ul className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3 text-ink-muted leading-relaxed">
            {ateliers.map((k) => (
              <Bullet key={k}>{t(k)}</Bullet>
            ))}
          </ul>
        </div>
      </section>

      {/* DATES & HORAIRES */}
      <section className="container-page pb-10">
        <div className="card-soft p-6 sm:p-8 mb-6 md:mb-8">
          <p className="text-xs uppercase tracking-[0.14em] text-forest-dark font-medium mb-2">
            {t("formation.conclusion_title")}
          </p>
          <p className="text-ink-muted leading-relaxed">{t("formation.conclusion_text")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <div className="space-y-6 md:space-y-8">
            <div className="card-soft p-6 sm:p-8" data-testid="formation-dates">
              <h3 className="font-heading text-xl sm:text-2xl text-ink mb-4">
                {t("formation.dates_title")}
              </h3>
              <ul className="space-y-3 text-ink-muted leading-relaxed">
                <Bullet>{t("formation.dates_1")}</Bullet>
                <Bullet>{t("formation.dates_2")}</Bullet>
              </ul>
            </div>
            <aside
              className="rounded-[1.25rem] border border-forest/15 bg-forest/5 p-6 sm:p-8"
              data-testid="formation-cancellation-policy"
            >
              <h3 className="font-heading text-xl sm:text-2xl text-ink mb-4">
                {t("formation.cancellation_title")}
              </h3>
              <p className="text-ink-muted leading-relaxed">{t("formation.cancellation_intro")}</p>
              <ul className="mt-5 space-y-3 text-ink-muted leading-relaxed">
                <Bullet>{t("formation.cancellation_option_1")}</Bullet>
                <Bullet>{t("formation.cancellation_option_2")}</Bullet>
                <Bullet>{t("formation.cancellation_option_3")}</Bullet>
              </ul>
            </aside>
          </div>
          <div className="card-soft p-6 sm:p-8" data-testid="formation-hours">
            <h3 className="font-heading text-xl sm:text-2xl text-ink mb-4">
              {t("formation.hours_title")}
            </h3>
            <ul className="space-y-3 text-ink-muted leading-relaxed">
              <Bullet>{t("formation.hours_sun")}</Bullet>
              <Bullet>{t("formation.hours_weekdays")}</Bullet>
              <Bullet>{t("formation.hours_wed")}</Bullet>
              <Bullet>{t("formation.hours_sat")}</Bullet>
            </ul>
          </div>
        </div>
      </section>

      {/* FORMATEURS */}
      <section className="container-page pb-10" data-testid="formation-formateurs">
        <h2 className="font-heading text-2xl sm:text-3xl text-ink mb-6">
          {t("formation.formateurs_title")}
        </h2>
        <div className="card-soft p-6 sm:p-8">
          <ul className="space-y-4 text-ink-muted leading-relaxed">
            {formateurs.map((k) => (
              <Bullet key={k}>{t(k)}</Bullet>
            ))}
          </ul>
          <p className="mt-5 text-ink-muted leading-relaxed">{t("formation.formateurs_team_note")}</p>
          <p className="mt-5 text-ink-muted italic leading-relaxed">
            {t("formation.formateur_salma")}
          </p>
        </div>
      </section>

      {/* TARIFS */}
      <section className="container-page pb-16" data-testid="formation-tarifs">
        <div className="rounded-[1.25rem] border border-forest/15 bg-forest/5 p-6 sm:p-8">
          <h2 className="font-heading text-2xl sm:text-3xl text-ink mb-4">
            {t("formation.tarifs_title")}
          </h2>
          <p className="font-heading text-3xl sm:text-4xl text-forest-dark font-medium">
            {t("formation.tarifs_price")}
          </p>
          <p className="mt-5 text-ink-muted leading-relaxed">
            {t("formation.tarifs_signup")}{" "}
            <a
              href={`mailto:${t("formation.tarifs_contact_email")}`}
              className="inline-flex items-center gap-1.5 text-forest hover:text-forest-dark underline underline-offset-4 break-all"
              data-testid="formation-contact-louise"
            >
              <Mail size={15} aria-hidden />
              {t("formation.tarifs_contact_email")}
            </a>
          </p>
          <p className="mt-3 text-ink-muted leading-relaxed">{t("formation.tarifs_deposit")}</p>
        </div>
      </section>
    </div>
  );
}
